import { QueryBuilder, WhereClause, OrderByClause } from './queryBuilder';
import { db } from '../firebaseConfig';
import {
  Firestore,
  DocumentChange,
  DocumentReference,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  QuerySnapshot,
  SnapshotListenOptions,
  addDoc,
  onSnapshot,
  getDocs,
  getDoc,
  limit,
  query,
  setDoc,
  deleteDoc,
  deleteField,
  doc,
  runTransaction,
  writeBatch,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import {
  InsuranceCaseData,
  InsuranceClaimData,
  EngagementLetterData,
  EventData,
  ExpenseEntryData,
  ExpenseEntryDocument,
  ExpenseEntryEvent,
  FinancialInstitutionData,
  FirmData,
  HumanData,
  InvoiceData,
  InvoiceEmailDocument,
  InvoicePresentationSnapshotData,
  KnowledgeBasePageData,
  MatterData,
  MatterDocumentData,
  NoteData,
  OrganizationData,
  TaskData,
  TemplateData,
  TimeEntryData,
  TimeEntryDocument,
  TimeEntryEvent,
  TransactionData,
  UserData,
  PaySheetData,
  ClioSyncAttemptData,
  ClioSyncAttemptBatchData,
  CommunicationEventData,
  CommunicationEventEvent,
  TransactionDocument,
  V3InvoiceData,
  V3InvoicePresentationSnapshotData,
  InvoiceEmailData,
  InvoiceTimelineEventDocument,
  InvoiceTimelineUserActor,
  InvoiceAllocationCoordinatorData,
  InvoiceTransactionAllocationData,
  MatterInvoiceBalanceEventData,
  MatterDraftInvoiceState,
} from '@shared/types';

import { cleanDataToFirestore } from './converters';
import { Timestamp } from 'firebase/firestore';
import { ymdFromLocalDate, ymdToFirestoreString } from './yearMonthDayUtil';
import {
  getTalkrouteCommunicationAssignmentCandidates,
  isAssignableTalkrouteCommunicationEvent,
  normalizeTalkroutePhoneNumbers,
  TalkrouteCommunicationEventRecord,
} from './talkrouteCommunicationAssignment';
import {
  deriveNextInvoiceNumberFromInvoices,
  DraftInvoiceSourceRef,
  finalizeImplicitMatterDraftInTransaction,
  revertFinalizedInvoiceToDraftInTransaction,
  restoreVoidedInvoiceInTransaction,
  voidFinalizedInvoiceInTransaction,
  type ApproveDraftInvoiceProps,
  type FinalizeImplicitMatterDraftResult,
  type RevertFinalizedInvoiceToDraftProps,
  type RevertFinalizedInvoiceToDraftResult,
  type RestoreVoidedInvoiceProps,
  type RestoreVoidedInvoiceResult,
  type VoidFinalizedInvoiceProps,
  type VoidFinalizedInvoiceResult,
} from './draftInvoiceCreation';
import {
  attachMatterDraftInvoiceSources as attachMatterDraftInvoiceSourcesAction,
  mutateMatterDraftInvoiceSource as mutateMatterDraftInvoiceSourceAction,
  updateMatterDraftInvoiceManualAdjustments as updateMatterDraftInvoiceManualAdjustmentsAction,
  type AttachMatterDraftInvoiceSourcesProps,
  type MutateMatterDraftInvoiceSourceProps,
  type UpdateMatterDraftInvoiceManualAdjustmentsProps,
} from './matterDraftInvoiceStateActions';
import { guardTopLevelSourceEntryMutation } from './sourceEntryMutationGuards';
import {
  deleteNonCurrentModelInvoiceInTransaction,
  setInvoiceWithoutLifecycleTransitionInTransaction,
} from './invoiceMutationGuards';
import { isTransactionOperationallyActive } from './transactionSoftDelete';

const invoiceTimelineEventWrite = (
  queryBuilder: QueryBuilder,
  actor: InvoiceTimelineUserActor,
) => ({
  actor,
  eventRef: doc(queryBuilder.invoiceTimelineEvents()),
  occurredTimestamp: new Date(),
});

export interface TimeEntryBasic {
  id: string;
  matter: string;
  user: string;
  timestamp: string;
}

export interface TransactionReadOptions {
  includeDeleted?: boolean;
}

const transactionDocumentChangesWithReadOptions = (
  snapshot: QuerySnapshot<TransactionData>,
  docs: QueryDocumentSnapshot<TransactionData>[],
  previousDocs: QueryDocumentSnapshot<TransactionData>[],
  options?: SnapshotListenOptions,
): DocumentChange<TransactionData>[] => {
  const activeDocumentIds = new Set(docs.map((doc) => doc.id));
  const previousActiveDocumentIds = new Set(previousDocs.map((doc) => doc.id));
  const changes: DocumentChange<TransactionData>[] = [];

  for (const change of snapshot.docChanges(options)) {
    const wasActive = previousActiveDocumentIds.has(change.doc.id);
    const isActive = activeDocumentIds.has(change.doc.id);
    if (!wasActive && !isActive) continue;

    if (!wasActive) {
      changes.push({
        type: 'added',
        doc: change.doc,
        oldIndex: -1,
        newIndex: docs.findIndex((doc) => doc.id === change.doc.id),
      });
      continue;
    }

    if (!isActive) {
      changes.push({
        type: 'removed',
        doc: change.doc,
        oldIndex: previousDocs.findIndex((doc) => doc.id === change.doc.id),
        newIndex: -1,
      });
      continue;
    }

    changes.push({
      type: change.type === 'removed' ? 'modified' : change.type,
      doc: change.doc,
      oldIndex: previousDocs.findIndex((doc) => doc.id === change.doc.id),
      newIndex: docs.findIndex((doc) => doc.id === change.doc.id),
    });
  }

  return changes;
};

const transactionSnapshotWithReadOptions = (
  snapshot: QuerySnapshot<TransactionData>,
  previousDocs: QueryDocumentSnapshot<TransactionData>[],
  options: TransactionReadOptions,
): QuerySnapshot<TransactionData> => {
  if (options.includeDeleted) return snapshot;

  const docs = snapshot.docs.filter((doc) =>
    isTransactionOperationallyActive(doc.data()),
  );
  const filteredSnapshot = Object.create(
    snapshot,
  ) as QuerySnapshot<TransactionData>;

  Object.defineProperties(filteredSnapshot, {
    docs: { value: docs },
    empty: { value: docs.length === 0 },
    size: { value: docs.length },
    forEach: {
      value: (
        callback: (result: QueryDocumentSnapshot<TransactionData>) => void,
        thisArg?: unknown,
      ) => {
        docs.forEach((doc) => callback.call(thisArg, doc));
      },
    },
    docChanges: {
      value: (snapshotOptions?: SnapshotListenOptions) =>
        transactionDocumentChangesWithReadOptions(
          snapshot,
          docs,
          previousDocs,
          snapshotOptions,
        ),
    },
  });

  return filteredSnapshot;
};

const transactionDocumentSnapshotWithReadOptions = (
  snapshot: DocumentSnapshot<TransactionData>,
  options: TransactionReadOptions,
): DocumentSnapshot<TransactionData> => {
  const data = snapshot.data();
  if (
    options.includeDeleted ||
    data === undefined ||
    isTransactionOperationallyActive(data)
  ) {
    return snapshot;
  }

  const filteredSnapshot = Object.create(
    snapshot,
  ) as DocumentSnapshot<TransactionData>;
  Object.defineProperties(filteredSnapshot, {
    exists: { value: () => false },
    data: { value: () => undefined },
    get: { value: () => undefined },
  });
  return filteredSnapshot;
};

type CommunicationEventCollection = 'phoneCalls' | 'textMessages';
type CommunicationEventEditableFields = Partial<
  Pick<CommunicationEventData, 'archived' | 'description' | 'matter' | 'user'>
>;

const getCommunicationEventRef = (
  db: FirmDatabase,
  props: { collection: CommunicationEventCollection; id: string },
) =>
  props.collection === 'phoneCalls'
    ? db.query.phoneCall(props.id)
    : db.query.textMessage(props.id);

const estimateCommunicationEventHours = (
  event: CommunicationEventData,
): number => {
  if (event.recordType === 'textMessage') {
    return 0.1;
  }
  const durationHours = (event.durationSeconds ?? 0) / 3600;
  return Math.max(0.1, Math.ceil(durationHours * 10) / 10);
};

const isImplicitMatterDraftTimeEntryCandidate = (
  entry: TimeEntryData,
  matterId: string,
) =>
  entry.matter === matterId &&
  entry.invoiceId === undefined &&
  entry.billingState !== 'unbillable' &&
  entry.status !== 'draft';

const isImplicitMatterDraftExpenseEntryCandidate = (
  entry: ExpenseEntryData,
  matterId: string,
) =>
  entry.matter === matterId &&
  entry.invoiceId === undefined &&
  entry.billingState !== 'unbillable';

export class FirmDatabase {
  firmId: string;
  firestore: Firestore;
  query: QueryBuilder;
  #invoiceRefForId = (invoiceId: string) =>
    this.query.invoice(invoiceId) as DocumentReference<V3InvoiceData>;

  constructor(firmId: string, firestore: Firestore = db) {
    this.firmId = firmId;
    this.firestore = firestore;
    this.query = new QueryBuilder(firmId, firestore);
  }

  // -------------------------------------------------------------------------
  // Base methods
  // -------------------------------------------------------------------------

  async addClioSyncAttempt(
    data: ClioSyncAttemptData,
  ): Promise<DocumentReference<ClioSyncAttemptData>> {
    return await addDoc(this.query.clioSyncAttempts(), data);
  }

  /**
   * @param {*} observer The function called when relevant clioSyncAttempts change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToClioSyncAttemptsChanges(
    observer: (snapshot: QuerySnapshot<ClioSyncAttemptData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.clioSyncAttempts(), observer);
    const subscriptionName = 'clioSyncAttempts';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  /**
   * @param {*} observer The function called when relevant clioSyncAttemptBatches change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToClioSyncAttemptBatchesChanges(
    syncAttemptId: string,
    observer: (snapshot: QuerySnapshot<ClioSyncAttemptBatchData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.clioSyncAttemptBatches(syncAttemptId),
      observer,
    );
    const subscriptionName = 'clioSyncAttemptBatches';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // engagementLetter methods

  async getEngagementLetters(
    where: WhereClause<EngagementLetterData>[] = [],
    orderBy: OrderByClause<EngagementLetterData>[] = [],
  ): Promise<QuerySnapshot<EngagementLetterData>> {
    return await getDocs(this.query.engagementLetters(where, orderBy));
  }

  /**
   * @returns A `DocumentSnapshot` object.
   */
  async getEngagementLetter(
    id: string,
  ): Promise<DocumentSnapshot<EngagementLetterData>> {
    return await getDoc(this.query.engagementLetter(id));
  }

  async setEngagementLetter(id: string, data: EngagementLetterData) {
    await setDoc(this.query.engagementLetter(id), data);
  }

  subscribeToEngagementLettersChanges(
    where: WhereClause<EngagementLetterData>[] = [],
    orderBy: OrderByClause<EngagementLetterData>[] = [],
    observer: (snapshot: QuerySnapshot<EngagementLetterData>) => void,
  ): () => void {
    return onSnapshot(this.query.engagementLetters(where, orderBy), observer);
  }

  // expenseEntries methods

  async getExpenseEntries(
    where: WhereClause<ExpenseEntryData>[] = [],
  ): Promise<QuerySnapshot<ExpenseEntryData>> {
    return await getDocs(this.query.expenseEntries(where, []));
  }

  async addExpenseEntry(
    data: ExpenseEntryData,
  ): Promise<DocumentReference<ExpenseEntryData>> {
    const createdBy = data.createdBy ?? data.user;
    const createdEvent: ExpenseEntryEvent = {
      type: 'created',
      timestamp: new Date(),
      userId: createdBy,
    };
    const expenseEntryData: ExpenseEntryData = {
      ...data,
      createdBy,
      events: [createdEvent, ...(data.events ?? [])],
    };
    return await addDoc(this.query.expenseEntries(), expenseEntryData);
  }

  async updateExpenseEntry(
    id: string,
    data: Partial<ExpenseEntryData>,
  ): Promise<void> {
    await runTransaction(this.firestore, async (transaction) => {
      const expenseEntryRef = this.query.expenseEntry(id);
      await guardTopLevelSourceEntryMutation({
        transaction,
        sourceRef: expenseEntryRef,
        invoiceRefForId: this.#invoiceRefForId,
        data,
        action: 'update',
      });
      transaction.update(expenseEntryRef, cleanDataToFirestore(data));
    });
  }

  async deleteExpenseEntry(id: string) {
    await runTransaction(this.firestore, async (transaction) => {
      const expenseEntryRef = this.query.expenseEntry(id);
      await guardTopLevelSourceEntryMutation({
        transaction,
        sourceRef: expenseEntryRef,
        invoiceRefForId: this.#invoiceRefForId,
        action: 'delete',
      });
      transaction.delete(expenseEntryRef);
    });
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @param {*} orderBy An array of [fieldPath, directionStr] tuples.
   * @param {*} observer The function called when relevant timeEntries change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToExpenseEntriesChanges(
    where: WhereClause<ExpenseEntryData>[],
    orderBy: OrderByClause<ExpenseEntryData>[],
    observer: (snapshot: QuerySnapshot<ExpenseEntryData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.expenseEntries(where, orderBy),
      observer,
    );
    const subscriptionName = 'expenseEntries';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToExpenseEntryChanges(
    id: string,
    observer: (doc: ExpenseEntryDocument | undefined) => void,
  ): () => void {
    const ref = this.query.expenseEntry(id);
    const pushDoc = (snap: DocumentSnapshot<ExpenseEntryData>) => {
      if (!snap.exists()) {
        observer(undefined);
        return;
      }
      const data = snap.data();
      observer(data ? { ...data, id: snap.id } : undefined);
    };
    getDoc(ref).then(pushDoc);
    const unsub = onSnapshot(ref, pushDoc);
    this.#incrementSubscriptionCount('expenseEntry');
    return () => {
      this.#decrementSubscriptionCount('expenseEntry');
      unsub();
    };
  }

  // -------------------------------------------------------------------------
  // financialInstitutions

  async getFinancialInstitution(
    id: string,
  ): Promise<DocumentSnapshot<FinancialInstitutionData>> {
    return await getDoc(this.query.financialInstitution(id));
  }

  async addFinancialInstitution(
    data: FinancialInstitutionData,
  ): Promise<DocumentReference<FinancialInstitutionData>> {
    return await addDoc(this.query.financialInstitutions(), data);
  }

  async updateFinancialInstitution(
    id: string,
    data: Partial<FinancialInstitutionData>,
  ) {
    await this.#updateDocument(this.query.financialInstitution(id), data);
  }

  async deleteFinancialInstitution(id: string) {
    await deleteDoc(this.query.financialInstitution(id));
  }

  subscribeToFinancialInstitutionsChanges(
    observer: (snapshot: QuerySnapshot<FinancialInstitutionData>) => void,
    where?: WhereClause<FinancialInstitutionData>[],
    orderBy?: OrderByClause<FinancialInstitutionData>[],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.financialInstitutions(where, orderBy),
      observer,
    );

    const subscriptionName = 'financialInstitutions';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // -------------------------------------------------------------------------
  // firms

  async getFirm(): Promise<DocumentSnapshot<FirmData>> {
    return await getDoc(this.query.firm());
  }

  async updateFirm(data: Partial<FirmData>) {
    await this.#updateDocument(this.query.firm(), data);
  }

  /**
   * @param {*} observer The function called when relevant firm data changes.
   * @returns The 'unsubscribe' function.
   */
  subscribeToFirmChanges(
    observer: (snapshot: DocumentSnapshot<FirmData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.firm(), observer);
    const subscriptionName = 'firm';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToInvoiceAllocationCoordinatorChanges(
    observer: (
      snapshot: DocumentSnapshot<InvoiceAllocationCoordinatorData>,
    ) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.invoiceAllocationCoordinator(),
      observer,
    );
    const subscriptionName = 'invoiceAllocationCoordinator';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // -------------------------------------------------------------------------
  // humans

  async addHuman(data: HumanData): Promise<DocumentReference<HumanData>> {
    return await addDoc(this.query.humans(), data);
  }

  async getHumans(
    where: WhereClause<HumanData>[] = [],
    orderBy: OrderByClause<HumanData>[] = [],
  ): Promise<QuerySnapshot<HumanData>> {
    return await getDocs(this.query.humans(where, orderBy));
  }

  async getHuman(id: string): Promise<DocumentSnapshot<HumanData>> {
    return await getDoc(this.query.human(id));
  }

  subscribeToHumanChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<HumanData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.human(id), observer);
    const subscriptionName = `humans/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToHumansChanges(
    observer: (snapshot: QuerySnapshot<HumanData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.humans(), observer);
    const subscriptionName = 'humans';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  async updateHuman(id: string, data: Partial<HumanData>) {
    await this.#updateDocument(this.query.human(id), data);
  }

  async deleteHuman(id: string) {
    await deleteDoc(this.query.human(id));
  }

  // invoices methods

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getInvoices(
    where: WhereClause<InvoiceData>[] = [],
  ): Promise<QuerySnapshot<InvoiceData>> {
    return await getDocs(this.query.invoices(where, []));
  }

  async setInvoice(id: string, data: InvoiceData): Promise<void> {
    await runTransaction(this.firestore, async (transaction) =>
      setInvoiceWithoutLifecycleTransitionInTransaction(
        transaction,
        this.query.invoice(id),
        data,
      ),
    );
  }

  async getInvoice(id: string): Promise<DocumentSnapshot<InvoiceData>> {
    return await getDoc(this.query.invoice(id));
  }

  async getInvoiceTransactionAllocations(
    where: WhereClause<InvoiceTransactionAllocationData>[] = [],
  ): Promise<QuerySnapshot<InvoiceTransactionAllocationData>> {
    return await getDocs(this.query.invoiceTransactionAllocations(where, []));
  }

  async getInvoicesListRows() {
    return await getDocs(this.query.invoicesListRows());
  }

  async getInvoicesListMatterUpdates() {
    return await getDocs(this.query.invoicesListMatterUpdates());
  }

  async getInvoicesListViewMetadata() {
    return await getDoc(this.query.invoicesListView());
  }

  async mutateMatterDraftInvoiceSource(
    props: MutateMatterDraftInvoiceSourceProps,
  ) {
    return await mutateMatterDraftInvoiceSourceAction(this, props);
  }

  async attachMatterDraftInvoiceSources(
    props: AttachMatterDraftInvoiceSourcesProps,
  ) {
    return await attachMatterDraftInvoiceSourcesAction(this, props);
  }

  async updateMatterDraftInvoiceManualAdjustments(
    props: UpdateMatterDraftInvoiceManualAdjustmentsProps,
  ) {
    return await updateMatterDraftInvoiceManualAdjustmentsAction(this, props);
  }

  async voidFinalizedInvoice(
    props: VoidFinalizedInvoiceProps,
  ): Promise<VoidFinalizedInvoiceResult> {
    const sourceRefs: DraftInvoiceSourceRef[] = [
      ...(props.timeEntryIds ?? []).map((entryId) => ({
        entryType: 'timeEntry' as const,
        entryId,
        ref: this.query.timeEntry(entryId) as DraftInvoiceSourceRef['ref'],
      })),
      ...(props.expenseEntryIds ?? []).map((entryId) => ({
        entryType: 'expenseEntry' as const,
        entryId,
        ref: this.query.expenseEntry(entryId) as DraftInvoiceSourceRef['ref'],
      })),
    ];
    const timelineEvent = invoiceTimelineEventWrite(this.query, props.actor);

    return await runTransaction(this.firestore, async (transaction) => {
      const invoiceRef = this.query.invoice(
        props.invoiceId,
      ) as DocumentReference<V3InvoiceData>;
      const firmSnapshot = await transaction.get(this.query.firm());
      if (
        firmSnapshot.data()?.invoiceAllocationCutover?.status === 'complete'
      ) {
        return { status: 'allocation-aware' };
      }
      return await voidFinalizedInvoiceInTransaction({
        transaction,
        invoiceRef,
        invoiceAllocationCoordinatorRef:
          this.query.invoiceAllocationCoordinator(),
        sourceRefs,
        timelineEvent,
        voidReason: props.voidReason,
      });
    });
  }

  async restoreVoidedInvoice(
    props: RestoreVoidedInvoiceProps,
  ): Promise<RestoreVoidedInvoiceResult> {
    const sourceRefs: DraftInvoiceSourceRef[] =
      props.expectedSourceFingerprints.map(({ entryId, entryType }) => ({
        entryType,
        entryId,
        ref:
          entryType === 'timeEntry'
            ? (this.query.timeEntry(entryId) as DraftInvoiceSourceRef['ref'])
            : (this.query.expenseEntry(
                entryId,
              ) as DraftInvoiceSourceRef['ref']),
      }));
    const timelineEvent = invoiceTimelineEventWrite(this.query, props.actor);

    return await runTransaction(this.firestore, async (transaction) =>
      restoreVoidedInvoiceInTransaction({
        transaction,
        invoiceRef: this.query.invoice(
          props.invoiceId,
        ) as DocumentReference<V3InvoiceData>,
        invoiceAllocationCoordinatorRef:
          this.query.invoiceAllocationCoordinator(),
        sourceRefs,
        expectedVoidedAt: props.expectedVoidedAt,
        expectedSourceFingerprints: props.expectedSourceFingerprints,
        timelineEvent,
      }),
    );
  }

  async revertFinalizedInvoiceToDraft(
    props: RevertFinalizedInvoiceToDraftProps,
  ): Promise<RevertFinalizedInvoiceToDraftResult> {
    const implicitDraft = {
      matterId: props.implicitDraft.matterId,
      draftStateRef: this.query.matterDraftInvoiceState(
        props.implicitDraft.matterId,
      ),
      sourceRefs: [
        ...props.implicitDraft.timeEntryIds.map((entryId) => ({
          entryType: 'timeEntry' as const,
          entryId,
          ref: this.query.timeEntry(entryId) as DraftInvoiceSourceRef['ref'],
        })),
        ...props.implicitDraft.expenseEntryIds.map((entryId) => ({
          entryType: 'expenseEntry' as const,
          entryId,
          ref: this.query.expenseEntry(entryId) as DraftInvoiceSourceRef['ref'],
        })),
      ],
      originalDraftState: props.implicitDraft.originalDraftState,
    };

    const timelineEvent = invoiceTimelineEventWrite(this.query, props.actor);
    return await runTransaction(this.firestore, async (transaction) =>
      revertFinalizedInvoiceToDraftInTransaction({
        transaction,
        invoiceRef: this.query.invoice(
          props.invoiceId,
        ) as DocumentReference<V3InvoiceData>,
        invoiceAllocationCoordinatorRef:
          this.query.invoiceAllocationCoordinator(),
        presentationSnapshotRef: this.query.invoicePresentationSnapshot(
          props.invoiceId,
          props.presentationSnapshotId,
        ),
        retainerTransferRef: props.retainerTransferId
          ? this.query.transaction(props.retainerTransferId)
          : undefined,
        expectedFinalizedTimestamp: props.expectedFinalizedTimestamp,
        timelineEvent,
        implicitDraft,
      }),
    );
  }

  async getNextInvoiceNumber(): Promise<number> {
    const [invoices, counter] = await Promise.all([
      this.getInvoices(),
      getDoc(this.query.invoiceNumberCounter()),
    ]);
    const counterNext = counter.data()?.nextInvoiceNumber;
    return Math.max(
      deriveNextInvoiceNumberFromInvoices(
        invoices.docs.map((invoice) => invoice.data()),
      ),
      typeof counterNext === 'number' && Number.isFinite(counterNext)
        ? counterNext
        : 1,
    );
  }

  async finalizeImplicitMatterDraft(props: {
    matterId: string;
    actor: ApproveDraftInvoiceProps['actor'];
    timeEntryIds?: string[];
    expenseEntryIds?: string[];
    expectedInvoiceNumber?: number;
    expectedDraftStateUpdatedTimestamp?: Date;
    finalizedFields: ApproveDraftInvoiceProps['finalizedFields'];
    presentationSnapshotData: ApproveDraftInvoiceProps['presentationSnapshotData'];
    retainerTransferId?: string;
  }): Promise<FinalizeImplicitMatterDraftResult> {
    const [invoices, timeEntries, expenseEntries] = await Promise.all([
      this.getInvoices(),
      this.getTimeEntries([['matter', '==', props.matterId]]),
      this.getExpenseEntries([['matter', '==', props.matterId]]),
    ]);
    const initialNextInvoiceNumber = deriveNextInvoiceNumberFromInvoices(
      invoices.docs.map((invoice) => invoice.data()),
    );
    const invoiceRef = doc(
      this.query.invoices(),
    ) as DocumentReference<V3InvoiceData>;
    const presentationSnapshotRef = doc(
      this.query.invoicePresentationSnapshots(invoiceRef.id),
    ) as DocumentReference<V3InvoicePresentationSnapshotData>;
    const sourceRefsByKey = new Map<string, DraftInvoiceSourceRef>();
    const addSourceRef = (
      entryType: 'timeEntry' | 'expenseEntry',
      entryId: string,
    ) => {
      sourceRefsByKey.set(`${entryType}:${entryId}`, {
        entryType,
        entryId,
        ref:
          entryType === 'timeEntry'
            ? (this.query.timeEntry(entryId) as DraftInvoiceSourceRef['ref'])
            : (this.query.expenseEntry(
                entryId,
              ) as DraftInvoiceSourceRef['ref']),
      });
    };
    for (const entry of timeEntries.docs) {
      if (isImplicitMatterDraftTimeEntryCandidate(entry.data(), props.matterId))
        addSourceRef('timeEntry', entry.id);
    }
    for (const entry of expenseEntries.docs) {
      if (
        isImplicitMatterDraftExpenseEntryCandidate(entry.data(), props.matterId)
      )
        addSourceRef('expenseEntry', entry.id);
    }
    for (const entryId of props.timeEntryIds ?? [])
      addSourceRef('timeEntry', entryId);
    for (const entryId of props.expenseEntryIds ?? [])
      addSourceRef('expenseEntry', entryId);
    const sourceRefs = [...sourceRefsByKey.values()];
    const retainerTransferRef =
      (props.finalizedFields.retainerFundsApplied ?? 0) > 0 &&
      props.retainerTransferId
        ? this.query.transaction(props.retainerTransferId)
        : undefined;
    const timelineEvent = invoiceTimelineEventWrite(this.query, props.actor);

    return await runTransaction(this.firestore, async (transaction) =>
      finalizeImplicitMatterDraftInTransaction({
        transaction,
        firmId: this.firmId,
        matterId: props.matterId,
        matterRef: this.query.matter(props.matterId),
        draftStateRef: this.query.matterDraftInvoiceState(props.matterId),
        invoiceRef,
        counterRef: this.query.invoiceNumberCounter(),
        invoiceAllocationCoordinatorRef:
          this.query.invoiceAllocationCoordinator(),
        presentationSnapshotRef,
        retainerTransferRef,
        sourceRefs,
        initialNextInvoiceNumber,
        expectedInvoiceNumber: props.expectedInvoiceNumber,
        expectedDraftStateUpdatedTimestamp:
          props.expectedDraftStateUpdatedTimestamp,
        finalizedFields: props.finalizedFields,
        presentationSnapshotData: props.presentationSnapshotData,
        timelineEvent,
      }),
    );
  }

  async addInvoicePresentationSnapshot(
    invoiceId: string,
    data: InvoicePresentationSnapshotData,
  ): Promise<DocumentReference<InvoicePresentationSnapshotData>> {
    return await addDoc(
      this.query.invoicePresentationSnapshots(invoiceId),
      data,
    );
  }

  async setInvoicePresentationSnapshot(
    invoiceId: string,
    presentationSnapshotId: string,
    data: InvoicePresentationSnapshotData,
  ): Promise<void> {
    await setDoc(
      this.query.invoicePresentationSnapshot(invoiceId, presentationSnapshotId),
      data,
    );
  }

  async getInvoicePresentationSnapshot(
    invoiceId: string,
    presentationSnapshotId: string,
  ): Promise<DocumentSnapshot<InvoicePresentationSnapshotData>> {
    return await getDoc(
      this.query.invoicePresentationSnapshot(invoiceId, presentationSnapshotId),
    );
  }

  async getInvoicePresentationSnapshots(
    invoiceId: string,
    orderBy: OrderByClause<InvoicePresentationSnapshotData>[] = [],
  ): Promise<QuerySnapshot<InvoicePresentationSnapshotData>> {
    return await getDocs(
      this.query.invoicePresentationSnapshots(invoiceId, orderBy),
    );
  }

  async deleteInvoice(id: string): Promise<void> {
    await runTransaction(this.firestore, async (transaction) =>
      deleteNonCurrentModelInvoiceInTransaction(
        transaction,
        this.query.invoice(id),
      ),
    );
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @param {*} observer The function called when relevant invoices change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToInvoicesChanges(
    where: WhereClause<InvoiceData>[],
    observer: (snapshot: QuerySnapshot<InvoiceData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.invoices(where, []), observer);
    const subscriptionName = 'invoices';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToInvoiceTransactionAllocationsChanges(
    where: WhereClause<InvoiceTransactionAllocationData>[],
    observer: (
      snapshot: QuerySnapshot<InvoiceTransactionAllocationData>,
    ) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.invoiceTransactionAllocations(where, []),
      observer,
      onError,
    );
    const subscriptionName = 'invoiceTransactionAllocations';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToInvoiceChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<InvoiceData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.invoice(id), observer);

    const subscriptionName = `invoices/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // knowledgeBasePage methods

  async addKnowledgeBasePage(
    data: KnowledgeBasePageData,
  ): Promise<DocumentReference<KnowledgeBasePageData>> {
    return await addDoc(this.query.knowledgeBasePages(), data);
  }

  async getKnowledgeBasePages(): Promise<QuerySnapshot<KnowledgeBasePageData>> {
    return await getDocs(this.query.knowledgeBasePages([], []));
  }

  subscribeToKnowledgeBasePagesChanges(
    observer: (snapshot: QuerySnapshot<KnowledgeBasePageData>) => void,
    where?: WhereClause<KnowledgeBasePageData>[],
    orderBy?: OrderByClause<KnowledgeBasePageData>[],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.knowledgeBasePages(where, orderBy),
      observer,
    );
    const subscriptionName = 'knowledgeBasePages';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  async updateKnowledgeBasePage(
    id: string,
    data: Partial<KnowledgeBasePageData>,
  ) {
    await this.#updateDocument(this.query.knowledgeBasePage(id), data);
  }

  async deleteKnowledgeBasePage(id: string) {
    await deleteDoc(this.query.knowledgeBasePage(id));
  }

  // matters methods

  async addMatter(data: MatterData): Promise<DocumentReference<MatterData>> {
    return await addDoc(this.query.matters(), data);
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getMatters(
    where: WhereClause<MatterData>[] = [],
    orderBy: OrderByClause<MatterData>[] = [],
  ): Promise<QuerySnapshot<MatterData>> {
    return await getDocs(this.query.matters(where, orderBy));
  }

  /**
   * @param {*} id The matter's ID.
   * @returns A `DocumentSnapshot` object.
   */
  async getMatter(id: string): Promise<DocumentSnapshot<MatterData>> {
    return await getDoc(this.query.matter(id));
  }

  async getMatterInvoiceBalanceEvents(
    where: WhereClause<MatterInvoiceBalanceEventData>[] = [],
  ): Promise<QuerySnapshot<MatterInvoiceBalanceEventData>> {
    return await getDocs(this.query.matterInvoiceBalanceEvents(where, []));
  }

  subscribeToMatterInvoiceBalanceEventsChanges(
    where: WhereClause<MatterInvoiceBalanceEventData>[],
    observer: (snapshot: QuerySnapshot<MatterInvoiceBalanceEventData>) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.matterInvoiceBalanceEvents(where, []),
      observer,
      onError,
    );
    const subscriptionName = 'matterInvoiceBalanceEvents';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  async getMatterDraftInvoiceState(
    matterId: string,
  ): Promise<DocumentSnapshot<MatterDraftInvoiceState>> {
    return await getDoc(this.query.matterDraftInvoiceState(matterId));
  }

  async getMatterDraftInvoiceStates(): Promise<
    QuerySnapshot<MatterDraftInvoiceState>
  > {
    return await getDocs(this.query.matterDraftInvoiceStates());
  }

  async setMatterDraftInvoiceState(
    matterId: string,
    data: MatterDraftInvoiceState,
  ): Promise<void> {
    await setDoc(this.query.matterDraftInvoiceState(matterId), data);
  }

  /**
   * Subscribes to changes in matter document.
   *
   * @param observer The function called when the specified matter changes.
   * @returns The 'unsubscribe' function.
   */
  subscribeToMatterChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<MatterData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.matter(id), observer);
    const subscriptionName = `matters/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  /**
   * Subscribes to changes in matters collection.
   *
   * @param observer The function called when relevant matters change.
   * @param where An array of [fieldPath, opStr, value] tuples to filter matters.
   * @param orderBy An array of [fieldPath, directionStr] tuples to order matters.
   * @returns The 'unsubscribe' function.
   */
  subscribeToMattersChanges(
    observer: (snapshot: QuerySnapshot<MatterData>) => void,
    where?: WhereClause<MatterData>[],
    orderBy?: OrderByClause<MatterData>[],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.matters(where, orderBy),
      observer,
    );
    const subscriptionName = 'matters';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  async updateMatter(id: string, data: Partial<MatterData>) {
    await this.#updateDocument(this.query.matter(id), data);
  }

  async updateMatterInvoiceability(
    matterId: string,
    isUninvoiceable: boolean,
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    batch.update(this.query.matter(matterId), {
      isUninvoiceable: isUninvoiceable ? true : deleteField(),
    });
    if (isUninvoiceable)
      batch.delete(this.query.matterDraftInvoiceState(matterId));
    await batch.commit();
  }

  /**
   * Persists a single provider's folder ID on a matter, preserving any
   * other provider's folder ID that's already set. Uses the dot-notation
   * field path overload of `updateDoc` so the update merges into the
   * `folderIds` object instead of replacing it.
   *
   * SharePoint stores `{ folderId, driveId }` so we can detect a
   * reconnect to a different drive. Google Drive stores just the
   * folder ID since folder IDs are globally addressable.
   */
  async setMatterFolderId(
    matterId: string,
    provider: 'sharePoint',
    value: { folderId: string; driveId: string },
  ): Promise<void>;
  async setMatterFolderId(
    matterId: string,
    provider: 'googleDrive',
    value: string,
  ): Promise<void>;
  async setMatterFolderId(
    matterId: string,
    provider: 'sharePoint' | 'googleDrive',
    value: string | { folderId: string; driveId: string },
  ): Promise<void> {
    await updateDoc(
      this.query.matter(matterId),
      `folderIds.${provider}`,
      value,
    );
  }

  /**
   * Removes a single provider's folder ID from a matter. Used when
   * the persisted ID points to a folder that no longer exists, the
   * wrong folder, or one the user can't access — clearing forces the
   * next load through the get-or-create path so the matter can
   * recover.
   */
  async clearMatterFolderId(
    matterId: string,
    provider: 'sharePoint' | 'googleDrive',
  ): Promise<void> {
    await updateDoc(
      this.query.matter(matterId),
      `folderIds.${provider}`,
      deleteField(),
    );
  }

  // matters/{id}/documents methods

  /**
   *
   * @param {*} matterId
   * @param {*} documentData
   * @returns A `DocumentReference` object.
   */
  async addMatterDocument(
    matterId: string,
    documentData: MatterDocumentData,
  ): Promise<DocumentReference<MatterDocumentData>> {
    return await addDoc(this.query.matterDocuments(matterId), documentData);
  }

  /**
   *
   * @param {*} matterId
   * @returns A `QuerySnapshot` object.
   */
  async getMatterDocuments(
    matterId: string,
  ): Promise<QuerySnapshot<MatterDocumentData>> {
    return await getDocs(this.query.matterDocuments(matterId));
  }

  /**
   * @param {*} matterId Self-explanatory
   * @param {*} orderDocumentsBy An array of [fieldPath, directionStr] tuples.
   * @param {*} observer The function called when relevant documents change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToMatterDocumentsChanges(
    matterId: string,
    orderDocumentsBy: OrderByClause<MatterDocumentData>[],
    observer: (snapshot: QuerySnapshot<MatterDocumentData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.matterDocuments(matterId, [], orderDocumentsBy),
      observer,
    );
    const subscriptionName = `matters/${matterId}/documents`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // matters/{id}/notes methods

  /**
   * @param {*} matterId Self-explanatory
   * @param {*} orderNotesBy An array of [fieldPath, directionStr] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getMatterNotes(
    matterId: string,
    orderNotesBy: OrderByClause<NoteData>[] = [],
  ): Promise<QuerySnapshot<NoteData>> {
    return await getDocs(this.query.matterNotes(matterId, [], orderNotesBy));
  }

  /**
   *
   * @param {*} matterId
   * @param {*} data
   * @returns A `DocumentReference` object.
   */
  async addMatterNote(
    matterId: string,
    data: NoteData,
  ): Promise<DocumentReference<NoteData>> {
    return await addDoc(this.query.matterNotes(matterId), data);
  }

  async updateMatterNote(
    matterId: string,
    noteId: string,
    data: Partial<NoteData>,
  ): Promise<void> {
    await this.#updateDocument(
      doc(this.query.matterNotes(matterId), noteId),
      data,
    );
  }

  async deleteMatterNote(matterId: string, noteId: string): Promise<void> {
    await deleteDoc(doc(this.query.matterNotes(matterId), noteId));
  }

  /**
   * @param {*} matterId Self-explanatory
   * @param {*} orderNotesBy An array of [fieldPath, directionStr] tuples.
   * @param {*} observer The function called when relevant notes change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToMatterNotesChanges(
    matterId: string,
    orderNotesBy: OrderByClause<NoteData>[],
    observer: (snapshot: QuerySnapshot<NoteData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.matterNotes(matterId, [], orderNotesBy),
      observer,
    );
    const subscriptionName = `matters/${matterId}/notes`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  /**
   * Subscribes to events at `firms/{firmId}/events` filtered by `matterId`.
   * Each matter accumulates events from cloud-function triggers (matter
   * created, status change, etc.).
   */
  subscribeToMatterEventsChanges(
    matterId: string,
    observer: (snapshot: QuerySnapshot<EventData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.events([['matterId', '==', matterId]]),
      observer,
    );
    const subscriptionName = `events?matterId=${matterId}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // insuranceCases methods

  async getInsuranceCases(
    where: WhereClause<InsuranceCaseData>[] = [],
    orderBy: OrderByClause<InsuranceCaseData>[] = [],
  ): Promise<QuerySnapshot<InsuranceCaseData>> {
    return await getDocs(this.query.insuranceCases(where, orderBy));
  }

  async getInsuranceCase(
    caseId: string,
  ): Promise<DocumentSnapshot<InsuranceCaseData>> {
    return await getDoc(this.query.insuranceCase(caseId));
  }

  async addInsuranceCase(
    data: InsuranceCaseData,
  ): Promise<DocumentReference<InsuranceCaseData>> {
    return await addDoc(this.query.insuranceCases(), data);
  }

  async updateInsuranceCase(
    caseId: string,
    data: Partial<InsuranceCaseData>,
  ): Promise<void> {
    await this.#updateDocument(this.query.insuranceCase(caseId), data);
  }

  async deleteInsuranceCase(caseId: string): Promise<void> {
    await deleteDoc(this.query.insuranceCase(caseId));
  }

  /**
   * Subscribes to changes in all insurance cases
   * @param observer The function called when insurance cases change
   * @param where An array of [fieldPath, opStr, value] tuples to filter cases
   * @param orderBy An array of [fieldPath, directionStr] tuples to order cases
   * @returns The 'unsubscribe' function
   */
  subscribeToInsuranceCasesChanges(
    observer: (snapshot: QuerySnapshot<InsuranceCaseData>) => void,
    where: WhereClause<InsuranceCaseData>[] = [],
    orderBy: OrderByClause<InsuranceCaseData>[] = [],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.insuranceCases(where, orderBy),
      observer,
    );
    const subscriptionName = `insuranceCases`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToInsuranceCaseChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<InsuranceCaseData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.insuranceCase(id), observer);
    const subscriptionName = `insuranceCases/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // insuranceClaims methods

  async getInsuranceClaims(
    where: WhereClause<InsuranceClaimData>[] = [],
    orderBy: OrderByClause<InsuranceClaimData>[] = [],
  ): Promise<QuerySnapshot<InsuranceClaimData>> {
    return await getDocs(this.query.insuranceClaims(where, orderBy));
  }

  async getInsuranceClaim(
    claimId: string,
  ): Promise<DocumentSnapshot<InsuranceClaimData>> {
    return await getDoc(this.query.insuranceClaim(claimId));
  }

  async setInsuranceClaim(claimId: string, data: InsuranceClaimData) {
    await setDoc(this.query.insuranceClaim(claimId), data);
  }

  async addInsuranceClaim(
    data: InsuranceClaimData,
  ): Promise<DocumentReference<InsuranceClaimData>> {
    return await addDoc(this.query.insuranceClaims(), data);
  }

  async updateInsuranceClaim(
    claimId: string,
    data: Partial<InsuranceClaimData>,
  ): Promise<void> {
    await this.#updateDocument(this.query.insuranceClaim(claimId), data);
  }

  async deleteInsuranceClaim(claimId: string): Promise<void> {
    await deleteDoc(this.query.insuranceClaim(claimId));
  }

  /**
   * Subscribes to changes in all insurance claims
   * @param observer The function called when insurance claims change
   * @param where An array of [fieldPath, opStr, value] tuples to filter claims
   * @param orderBy An array of [fieldPath, directionStr] tuples to order claims
   * @returns The 'unsubscribe' function
   */
  subscribeToInsuranceClaimsChanges(
    observer: (snapshot: QuerySnapshot<InsuranceClaimData>) => void,
    where: WhereClause<InsuranceClaimData>[] = [],
    orderBy: OrderByClause<InsuranceClaimData>[] = [],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.insuranceClaims(where, orderBy),
      observer,
    );
    const subscriptionName = `insuranceClaims`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToInsuranceClaimChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<InsuranceClaimData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.insuranceClaim(id), observer);
    const subscriptionName = `insuranceClaims/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // `organizations` methods

  async addOrganization(
    data: OrganizationData,
  ): Promise<DocumentReference<OrganizationData>> {
    return await addDoc(this.query.organizations(), data);
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getOrganizations(
    where: WhereClause<OrganizationData>[] = [],
  ): Promise<QuerySnapshot<OrganizationData>> {
    return await getDocs(this.query.organizations(where, []));
  }

  /**
   * @returns A `DocumentSnapshot` object.
   */
  async getOrganization(
    id: string,
  ): Promise<DocumentSnapshot<OrganizationData>> {
    return await getDoc(this.query.organization(id));
  }

  subscribeToOrganizationChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<OrganizationData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.organization(id), observer);
    const subscriptionName = `organizations/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToOrganizationsChanges(
    observer: (snapshot: QuerySnapshot<OrganizationData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.organizations(), observer);
    const subscriptionName = 'organizations';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  async updateOrganization(id: string, data: Partial<OrganizationData>) {
    await this.#updateDocument(this.query.organization(id), data);
  }

  async deleteOrganization(id: string) {
    await deleteDoc(this.query.organization(id));
  }

  // `tasks` methods

  async addTask(data: TaskData): Promise<DocumentReference<TaskData>> {
    return await addDoc(this.query.tasks(), data);
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getTasks(
    where: WhereClause<TaskData>[] = [],
    orderBy: OrderByClause<TaskData>[] = [],
  ): Promise<QuerySnapshot<TaskData>> {
    return await getDocs(this.query.tasks(where, orderBy));
  }

  async getTask(id: string): Promise<DocumentSnapshot<TaskData>> {
    return await getDoc(this.query.task(id));
  }

  async updateTask(id: string, data: Partial<TaskData>) {
    await this.#updateDocument(this.query.task(id), data);
  }

  async updateTasks(data: Partial<TaskData>, where?: WhereClause<TaskData>[]) {
    const tasks = await getDocs(this.query.tasks(where));
    const batch = writeBatch(this.firestore);

    for (const task of tasks.docs) {
      batch.update(task.ref, cleanDataToFirestore(data));
    }
    await batch.commit();
  }

  async deleteTask(id: string) {
    await deleteDoc(this.query.task(id));
  }

  /**
   * @param {*} observer The function called when relevant tasks change.
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @param {*} orderBy An array of [fieldPath, directionStr] tuples.
   * @returns The 'unsubscribe' function.
   */
  subscribeToTasksChanges(
    observer: (snapshot: QuerySnapshot<TaskData>) => void,
    where?: WhereClause<TaskData>[],
    orderBy?: OrderByClause<TaskData>[],
  ): () => void {
    const unsubscribe = onSnapshot(this.query.tasks(where, orderBy), observer);
    const subscriptionName = 'tasks';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // `templates` methods

  async addTemplate(
    data: TemplateData,
  ): Promise<DocumentReference<TemplateData>> {
    return await addDoc(this.query.templates(), data);
  }

  /**
   * Transactional create-if-absent for a template doc.
   *
   * Reads the doc by ID inside the transaction and only writes when nothing is
   * there. Returns the existing data if the doc was already populated (so a
   * caller seeding defaults won't clobber a firm's saved customizations), or
   * the supplied `data` when this call actually performed the write.
   *
   * Why a transaction instead of `setDoc(..., { merge: true })`: merge is
   * field-level, so a default-section payload would still overwrite a firm's
   * customized `sections` array. A transactional get-then-set is the only
   * shape that's truly "create if absent."
   */
  async createTemplateIfAbsent(
    id: string,
    data: TemplateData,
  ): Promise<TemplateData> {
    return runTransaction(this.firestore, async (tx) => {
      const ref = this.query.template(id);
      const snap = await tx.get(ref);
      const existing = snap.data();
      if (existing) return existing;
      tx.set(ref, data);
      return data;
    });
  }

  async getTemplates(
    where: WhereClause<TemplateData>[] = [],
    orderBy: OrderByClause<TemplateData>[] = [],
  ): Promise<QuerySnapshot<TemplateData>> {
    return await getDocs(this.query.templates(where, orderBy));
  }

  async getTemplate(id: string): Promise<DocumentSnapshot<TemplateData>> {
    return await getDoc(this.query.template(id));
  }

  async updateTemplate(id: string, data: Partial<TemplateData>): Promise<void> {
    await this.#updateDocument(this.query.template(id), data);
  }

  async deleteTemplate(id: string) {
    await deleteDoc(this.query.template(id));
  }

  subscribeToTemplatesChanges(
    observer: (snapshot: QuerySnapshot<TemplateData>) => void,
    where?: WhereClause<TemplateData>[],
    orderBy?: OrderByClause<TemplateData>[],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.templates(where, orderBy),
      observer,
    );
    const subscriptionName = 'templates';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // `phoneCalls` and `textMessages` methods

  async updateCommunicationEventAndAppendEvents(props: {
    collection: CommunicationEventCollection;
    id: string;
    data: CommunicationEventEditableFields;
    events: CommunicationEventEvent[];
    shouldUpdate?: (event: CommunicationEventData) => boolean;
  }): Promise<boolean> {
    const ref =
      props.collection === 'phoneCalls'
        ? this.query.phoneCall(props.id)
        : this.query.textMessage(props.id);
    return await runTransaction(this.firestore, async (tx) => {
      const snapshot = await tx.get(ref);
      const communicationEvent = snapshot.data();
      if (!communicationEvent) {
        throw new Error('Communication event not found');
      }
      if (props.shouldUpdate && !props.shouldUpdate(communicationEvent)) {
        return false;
      }
      if (communicationEvent.timeEntry) {
        throw new Error(
          'Cannot edit a communication event with a linked time entry',
        );
      }
      tx.update(
        ref,
        cleanDataToFirestore({
          ...props.data,
          updatedTimestamp: new Date(),
          events: arrayUnion(...props.events),
        } as unknown as Partial<CommunicationEventData>),
      );
      return true;
    });
  }

  async getTalkrouteCommunicationAssignmentCandidates(
    phoneNumbers: string[],
  ): Promise<TalkrouteCommunicationEventRecord[]> {
    const [phoneCalls, textMessages] = await Promise.all([
      getDocs(this.query.phoneCalls([['source', '==', 'talkroute']])),
      getDocs(this.query.textMessages([['source', '==', 'talkroute']])),
    ]);
    const recordsFor = (
      docs: typeof phoneCalls.docs,
      collection: CommunicationEventCollection,
    ): TalkrouteCommunicationEventRecord[] =>
      docs.map((doc) => ({ collection, id: doc.id, event: doc.data() }));
    return getTalkrouteCommunicationAssignmentCandidates(
      [
        ...recordsFor(phoneCalls.docs, 'phoneCalls'),
        ...recordsFor(textMessages.docs, 'textMessages'),
      ],
      phoneNumbers,
    );
  }

  async assignTalkrouteCommunicationEventsToUser(props: {
    phoneNumbers: string[];
    userId: string;
    actingUserId: string;
  }): Promise<number> {
    const normalizedPhoneNumbers = new Set(
      normalizeTalkroutePhoneNumbers(props.phoneNumbers),
    );
    const candidates = await this.getTalkrouteCommunicationAssignmentCandidates(
      props.phoneNumbers,
    );
    const timestamp = new Date();
    const results = await Promise.all(
      candidates.map((candidate) =>
        this.updateCommunicationEventAndAppendEvents({
          collection: candidate.collection,
          id: candidate.id,
          data: { user: props.userId },
          events: [
            {
              type: 'modified',
              timestamp,
              userId: props.actingUserId,
              details: `user changed from ${candidate.event.user ?? ''} to ${props.userId}`,
            },
          ],
          shouldUpdate: (event) =>
            isAssignableTalkrouteCommunicationEvent(
              event,
              normalizedPhoneNumbers,
            ),
        }),
      ),
    );
    return results.filter(Boolean).length;
  }

  // `timeEntries` methods

  async createTimeEntryFromCommunicationEvent(props: {
    collection: CommunicationEventCollection;
    id: string;
    rate: number;
    createdBy: string;
  }): Promise<DocumentReference<TimeEntryData>> {
    const communicationRef = getCommunicationEventRef(this, props);
    const timeEntryRef = doc(this.query.timeEntries());
    await runTransaction(this.firestore, async (tx) => {
      const snapshot = await tx.get(communicationRef);
      const event = snapshot.data();
      if (!event) throw new Error('Communication event not found');
      if (event.timeEntry)
        throw new Error('Communication already has a time entry');
      if (event.archived)
        throw new Error(
          'Cannot create a time entry for an archived communication',
        );
      if (!event.matter || !event.user)
        throw new Error('Communication must have a matter and user');
      const timestamp = new Date();
      tx.set(timeEntryRef, {
        earnedYmd: ymdFromLocalDate(event.occurredAt),
        createdTimestamp: timestamp,
        description: event.description,
        hours: estimateCommunicationEventHours(event),
        matter: event.matter,
        rate: props.rate,
        user: event.user,
        status: 'draft',
        createdBy: props.createdBy,
        sourceRecord: {
          type: 'talkroute',
          recordType: event.recordType,
          recordId: event.recordId,
          recordKey: event.recordKey,
          communicationEventId: props.id,
          talkroutePhoneNumber: event.talkroutePhoneNumber,
          otherPartyPhoneNumber: event.otherPartyPhoneNumber,
          occurredAt: event.occurredAt,
          direction: event.direction,
          durationSeconds: event.durationSeconds,
          conversationId: event.conversationId,
        },
        events: [{ type: 'created', timestamp, userId: props.createdBy }],
      });
      tx.update(
        communicationRef,
        cleanDataToFirestore({
          timeEntry: timeEntryRef.id,
          updatedTimestamp: timestamp,
          events: arrayUnion({
            type: 'modified',
            timestamp,
            userId: props.createdBy,
            details: `timeEntry changed from  to ${timeEntryRef.id}`,
          }),
        } as unknown as Partial<CommunicationEventData>),
      );
    });
    return timeEntryRef;
  }

  async addTimeEntry(
    data: TimeEntryData,
  ): Promise<DocumentReference<TimeEntryData>> {
    const createdBy = data.createdBy ?? data.user;
    const createdEvent: TimeEntryEvent = {
      type: 'created',
      timestamp: new Date(),
      userId: createdBy,
    };
    const timeEntryData: TimeEntryData = {
      ...data,
      createdBy,
      events: [createdEvent, ...(data.events ?? [])],
    };
    const timeEntryRef = doc(this.query.timeEntries());
    await setDoc(timeEntryRef, timeEntryData);
    return timeEntryRef;
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getTimeEntries(
    where: WhereClause<TimeEntryData>[] = [],
  ): Promise<QuerySnapshot<TimeEntryData>> {
    return await getDocs(this.query.timeEntries(where));
  }

  async updateTimeEntry(
    id: string,
    data: Partial<TimeEntryData>,
  ): Promise<void> {
    await runTransaction(this.firestore, async (transaction) => {
      const timeEntryRef = this.query.timeEntry(id);
      await guardTopLevelSourceEntryMutation({
        transaction,
        sourceRef: timeEntryRef,
        invoiceRefForId: this.#invoiceRefForId,
        data,
        action: 'update',
      });
      transaction.update(timeEntryRef, cleanDataToFirestore(data));
    });
  }

  async updateTimeEntryAndAppendEvents(
    id: string,
    data: Partial<TimeEntryData>,
    events: TimeEntryEvent[],
  ): Promise<void> {
    await runTransaction(this.firestore, async (transaction) => {
      const timeEntryRef = this.query.timeEntry(id);
      await guardTopLevelSourceEntryMutation({
        transaction,
        sourceRef: timeEntryRef,
        invoiceRefForId: this.#invoiceRefForId,
        data,
        action: 'update',
      });
      transaction.update(
        timeEntryRef,
        cleanDataToFirestore({
          ...data,
          events: arrayUnion(...events),
        } as unknown as Partial<TimeEntryData>),
      );
    });
  }

  async deleteTimeEntry(id: string, deletedBy?: string) {
    await runTransaction(this.firestore, async (tx) => {
      const timeEntryRef = this.query.timeEntry(id);
      const snapshot = await tx.get(timeEntryRef);
      const timeEntry = snapshot.data();
      await guardTopLevelSourceEntryMutation({
        transaction: tx,
        sourceRef: timeEntryRef,
        invoiceRefForId: this.#invoiceRefForId,
        action: 'delete',
      });
      if (
        timeEntry?.sourceRecord?.type === 'talkroute' &&
        timeEntry.sourceRecord.communicationEventId
      ) {
        const communicationRef = getCommunicationEventRef(this, {
          collection:
            timeEntry.sourceRecord.recordType === 'call'
              ? 'phoneCalls'
              : 'textMessages',
          id: timeEntry.sourceRecord.communicationEventId,
        });
        const communicationSnapshot = await tx.get(communicationRef);
        if (communicationSnapshot.data()?.timeEntry === id) {
          const timestamp = new Date();
          tx.update(
            communicationRef,
            cleanDataToFirestore({
              timeEntry: deleteField(),
              updatedTimestamp: timestamp,
              events: arrayUnion({
                type: 'modified',
                timestamp,
                userId: deletedBy ?? timeEntry.user,
                details: `timeEntry cleared after deleting ${id}`,
              }),
            } as unknown as Partial<CommunicationEventData>),
          );
        }
      }
      tx.delete(timeEntryRef);
    });
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @param {*} orderBy An array of [fieldPath, directionStr] tuples.
   * @param {*} observer The function called when relevant timeEntries change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToTimeEntriesChanges(
    where: WhereClause<TimeEntryData>[],
    orderBy: OrderByClause<TimeEntryData>[],
    observer: (snapshot: QuerySnapshot<TimeEntryData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.timeEntries(where, orderBy),
      observer,
    );
    this.#incrementSubscriptionCount('timeEntries');
    return () => {
      this.#decrementSubscriptionCount('timeEntries');
      unsubscribe();
    };
  }

  subscribeToTimeEntryChanges(
    id: string,
    observer: (doc: TimeEntryDocument | undefined) => void,
  ): () => void {
    const ref = this.query.timeEntry(id);
    const pushDoc = (snap: DocumentSnapshot<TimeEntryData>) => {
      if (!snap.exists()) {
        observer(undefined);
        return;
      }
      const data = snap.data();
      observer(data ? { ...data, id: snap.id } : undefined);
    };
    getDoc(ref).then(pushDoc);
    const unsub = onSnapshot(ref, pushDoc);
    this.#incrementSubscriptionCount('timeEntry');
    return () => {
      this.#decrementSubscriptionCount('timeEntry');
      unsub();
    };
  }

  // `paySheets` methods

  /**
   * @param {*} data
   * @returns A `DocumentReference` object.
   */

  async addPaySheet(
    data: PaySheetData,
  ): Promise<DocumentReference<PaySheetData>> {
    return await addDoc(this.query.paySheets(), data);
  }

  async getPaySheets(
    where: WhereClause<PaySheetData>[] = [],
    orderBy: OrderByClause<PaySheetData>[] = [],
  ): Promise<QuerySnapshot<PaySheetData>> {
    return await getDocs(this.query.paySheets(where, orderBy));
  }

  async updatePaySheet(
    paySheetId: string,
    data: Partial<PaySheetData>,
  ): Promise<void> {
    const paySheetRef = this.query.paySheet(paySheetId);

    await this.#updateDocument(paySheetRef, data);
  }

  async #updateDocument<T>(ref: DocumentReference<T>, data: Partial<T>) {
    await updateDoc(ref, cleanDataToFirestore(data));
  }

  //for pay sheet details
  async getTimeEntriesForMatters(matterIds: string[]) {
    const batchSize = 30;
    const timeEntries: TimeEntryBasic[] = [];
    for (let i = 0; i < matterIds.length; i += batchSize) {
      const batchRef = this.query.timeEntries([
        ['matter', 'in', matterIds.slice(i, i + batchSize)],
      ]);

      const snapshot = await getDocs(batchRef);
      timeEntries.push(
        ...snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            matter: data.matter,
            user: data.user,
            timestamp: ymdToFirestoreString(data.earnedYmd),
          };
        }),
      );
    }

    return timeEntries;
  }

  // for pay sheet details
  async getPreviousTransactionForMatter(
    matterId: string,
    currentTransactionDate: Date,
  ) {
    console.log('Getting previous transaction:', {
      matterId,
      currentTransactionDate: currentTransactionDate.toISOString(),
    });

    // Just get transactions for this matter
    const snapshot = await this.getTransactions([['matter', '==', matterId]]);

    // Sort and filter in memory
    const docs = snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((t) => {
        if (!t.createdTimestamp) return false;
        const timestamp =
          t.createdTimestamp instanceof Timestamp
            ? t.createdTimestamp.toDate()
            : new Date(t.createdTimestamp);
        return timestamp < currentTransactionDate;
      })
      .sort((a, b) => {
        if (!a.createdTimestamp || !b.createdTimestamp) return 0;
        const dateA =
          a.createdTimestamp instanceof Timestamp
            ? a.createdTimestamp.toDate()
            : new Date(a.createdTimestamp);
        const dateB =
          b.createdTimestamp instanceof Timestamp
            ? b.createdTimestamp.toDate()
            : new Date(b.createdTimestamp);
        return dateB.getTime() - dateA.getTime(); // descending
      });

    return (docs[0] as TransactionDocument) || null;
  }

  async getTimeEntriesForDateRange(
    userId: string,
    startDate: Date,
    endDate: Date,
    matterId: string,
  ): Promise<Array<{ hours: number; amount: number }>> {
    // Format dates to match earnedYmd format: ymd-YYYY-MM-DD
    const startYmd = `ymd-${startDate.toISOString().slice(0, 10)}`; // ymd-YYYY-MM-DD
    const endYmd = `ymd-${endDate.toISOString().slice(0, 10)}`;

    const timeEntriesRef = this.query.timeEntries([
      ['user', '==', userId],
      ['matter', '==', matterId],
      ['earnedYmd', '>=', startYmd],
      ['earnedYmd', '<=', endYmd],
    ]);

    const snapshot = await getDocs(timeEntriesRef);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        hours: Number(data.hours || 0),
        amount: Number(data.rate ?? 0) * Number(data.hours ?? 0),
      };
    });
  }

  async getExpenseEntriesForDateRange(
    startDate: Date,
    endDate: Date,
    matterId: string,
  ): Promise<Array<{ amount: number }>> {
    // Format dates to match incurredYmd format: ymd-YYYY-MM-DD
    const startYmd = `ymd-${startDate.toISOString().slice(0, 10)}`;
    const endYmd = `ymd-${endDate.toISOString().slice(0, 10)}`;

    const expenseEntriesRef = this.query.expenseEntries([
      ['matter', '==', matterId],
      ['incurredYmd', '>=', startYmd],
      ['incurredYmd', '<=', endYmd],
    ]);

    const snapshot = await getDocs(expenseEntriesRef);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      // Handle amount as either string or number
      const amount =
        typeof data.amount === 'string'
          ? parseFloat(data.amount)
          : Number(data.amount || 0);
      return { amount };
    });
  }

  // `transactions` methods

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @returns A `QuerySnapshot` object.
   */
  async getTransactions(
    where: WhereClause<TransactionData>[] = [],
    options: TransactionReadOptions = {},
  ): Promise<QuerySnapshot<TransactionData>> {
    const snapshot = await getDocs(this.query.transactions(where, []));
    return transactionSnapshotWithReadOptions(snapshot, [], options);
  }

  /**
   * Client-owned view markers are not financial mutations. Keep this narrow so
   * every allocation-affecting transaction write stays server-owned.
   */
  async updateTransactionViewState(
    transactionId: string,
    data: Partial<Pick<TransactionData, 'draftViewedBy' | 'failedViewedBy'>>,
  ): Promise<void> {
    await this.#updateDocument(this.query.transaction(transactionId), data);
  }

  /**
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @param {*} observer The function called when relevant transactions change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToTransactionsChanges(
    where: WhereClause<TransactionData>[],
    observer: (snapshot: QuerySnapshot<TransactionData>) => void,
    options: TransactionReadOptions = {},
  ): () => void {
    let previousDocs: QueryDocumentSnapshot<TransactionData>[] = [];
    const unsubscribe = onSnapshot(
      this.query.transactions(where, []),
      (snapshot) => {
        const filteredSnapshot = transactionSnapshotWithReadOptions(
          snapshot,
          previousDocs,
          options,
        );
        previousDocs = filteredSnapshot.docs;
        observer(filteredSnapshot);
      },
    );
    const subscriptionName = 'transactions';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  subscribeToTransactionChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<TransactionData>) => void,
    options: TransactionReadOptions = {},
  ): () => void {
    const unsubscribe = onSnapshot(this.query.transaction(id), (snapshot) => {
      observer(transactionDocumentSnapshotWithReadOptions(snapshot, options));
    });
    const subscriptionName = `transactions/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // `users` methods

  async addUser(data: UserData): Promise<DocumentReference<UserData>> {
    return await addDoc(this.query.users(), data);
  }

  async getUsers(
    where: WhereClause<UserData>[] = [],
    orderBy: OrderByClause<UserData>[] = [],
  ): Promise<QuerySnapshot<UserData>> {
    return await getDocs(this.query.users(where, orderBy));
  }

  async getUser(id: string): Promise<DocumentSnapshot<UserData>> {
    return await getDoc(this.query.user(id));
  }

  /**
   * @param {*} id The ID of the user to subscribe to.
   * @param {*} observer The function called when relevant users change.
   * @returns The 'unsubscribe' function.
   */
  subscribeToUserChanges(
    id: string,
    observer: (snapshot: DocumentSnapshot<UserData>) => void,
  ): () => void {
    const unsubscribe = onSnapshot(this.query.user(id), observer);
    const subscriptionName = `users/${id}`;
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  async updateUser(id: string, data: Partial<UserData>) {
    await this.#updateDocument(this.query.user(id), data);
  }

  /**
   * @param {*} observer The function called when relevant users change.
   * @param {*} where An array of [fieldPath, opStr, value] tuples.
   * @param {*} orderBy An array of [fieldPath, directionStr] tuples.
   * @returns The 'unsubscribe' function.
   */
  subscribeToUsersChanges(
    observer: (snapshot: QuerySnapshot<UserData>) => void,
    where?: WhereClause<UserData>[],
    orderBy?: OrderByClause<UserData>[],
  ): () => void {
    const unsubscribe = onSnapshot(this.query.users(where, orderBy), observer);
    const subscriptionName = 'users';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // `invoiceEmails` methods

  async addInvoiceEmails(
    data: InvoiceEmailData[],
  ): Promise<DocumentReference<InvoiceEmailData>[]> {
    const batch = writeBatch(this.firestore);
    const emailRefs: DocumentReference<InvoiceEmailData>[] = [];
    for (const email of data) {
      const emailRef = doc(this.query.invoiceEmails());
      emailRefs.push(emailRef);
      batch.set(emailRef, email);
    }
    await batch.commit();
    return emailRefs;
  }

  async deleteInvoiceEmail(emailId: string) {
    await deleteDoc(this.query.invoiceEmail(emailId));
  }

  async updateInvoiceEmail(emailId: string, data: Partial<InvoiceEmailData>) {
    await updateDoc(this.query.invoiceEmail(emailId), data);
  }

  /**
   * Applies invoice email updates and creates in one Firestore batch.
   *
   * This is used by invoice email promotion to keep draft->readyToSend
   * transitions atomic. If writes were split across multiple calls, failures in
   * the middle could leave some invoices readyToSend while others are not,
   * causing duplicate readyToSend docs when the user retries.
   * Returns the created email ids in the same order as `creates`.
   */
  async batchWriteInvoiceEmails(props: {
    updates: { emailId: string; data: Partial<InvoiceEmailData> }[];
    creates: InvoiceEmailData[];
  }): Promise<string[]> {
    const { updates, creates } = props;
    const batch = writeBatch(this.firestore);
    const createdEmailRefs: DocumentReference<InvoiceEmailData>[] = [];

    for (const update of updates) {
      batch.update(
        this.query.invoiceEmail(update.emailId),
        cleanDataToFirestore(update.data),
      );
    }
    for (const email of creates) {
      const emailRef = doc(this.query.invoiceEmails());
      createdEmailRefs.push(emailRef);
      batch.set(emailRef, email);
    }

    await batch.commit();
    return createdEmailRefs.map(({ id }) => id);
  }

  async getInvoiceEmails(
    where: WhereClause<InvoiceEmailData>[] = [],
    orderBy: OrderByClause<InvoiceEmailData>[] = [],
  ): Promise<QuerySnapshot<InvoiceEmailData>> {
    return await getDocs(this.query.invoiceEmails(where, orderBy));
  }

  async getInvoiceEmailsForInvoice(
    invoiceId: string,
  ): Promise<InvoiceEmailDocument[]> {
    const [byInvoiceIdSnapshot, legacySnapshot] = await Promise.all([
      this.getInvoiceEmails([['invoiceId', '==', invoiceId]]),
      this.getInvoiceEmails([['invoiceNumber', '==', invoiceId]]),
    ]);
    const emailsById = new Map<string, InvoiceEmailDocument>();
    legacySnapshot.docs.forEach((emailSnapshot) => {
      const email = emailSnapshot.data();
      // V3 records must follow invoiceId; invoiceNumber is only a legacy fallback.
      if (email.invoiceId !== undefined) return;
      emailsById.set(emailSnapshot.id, {
        id: emailSnapshot.id,
        ...email,
      });
    });
    byInvoiceIdSnapshot.docs.forEach((emailSnapshot) => {
      emailsById.set(emailSnapshot.id, {
        id: emailSnapshot.id,
        ...emailSnapshot.data(),
      });
    });
    return [...emailsById.values()];
  }

  // `invoiceTimelineEvents` methods

  async getInvoiceTimelineEvents(
    invoiceId: string,
  ): Promise<InvoiceTimelineEventDocument[]> {
    const timelineQuery = query(
      this.query.invoiceTimelineEvents(
        [['invoiceId', '==', invoiceId]],
        [['occurredTimestamp', 'desc']],
      ),
      limit(50),
    );
    const snapshot = await getDocs(timelineQuery);
    return snapshot.docs.map((eventSnapshot) => ({
      id: eventSnapshot.id,
      ...eventSnapshot.data(),
    }));
  }

  /**
   * @param observer The function called when relevant invoiceEmails change.
   * @param where An array of [fieldPath, opStr, value] tuples to filter invoiceEmails.
   * @param orderBy An array of [fieldPath, directionStr] tuples to order invoiceEmails.
   * @returns The 'unsubscribe' function.
   */
  subscribeToInvoiceEmailsChanges(
    observer: (snapshot: QuerySnapshot<InvoiceEmailData>) => void,
    where: WhereClause<InvoiceEmailData>[] = [],
    orderBy: OrderByClause<InvoiceEmailData>[] = [],
  ): () => void {
    const unsubscribe = onSnapshot(
      this.query.invoiceEmails(where, orderBy),
      observer,
    );
    const subscriptionName = 'invoiceEmails';
    this.#incrementSubscriptionCount(subscriptionName);
    return () => {
      unsubscribe();
      this.#decrementSubscriptionCount(subscriptionName);
    };
  }

  // ---------------------------------------------------------------------------
  // Logging
  // ---------------------------------------------------------------------------

  readonly activeSubscriptionCounts: { [name: string]: number } = {};

  logSubscriptionCount() {
    console.table(this.activeSubscriptionCounts);
  }

  #incrementSubscriptionCount(name: string) {
    this.activeSubscriptionCounts[name] = this.activeSubscriptionCounts[name]
      ? this.activeSubscriptionCounts[name] + 1
      : 1;
  }

  #decrementSubscriptionCount(name: string) {
    this.activeSubscriptionCounts[name] = this.activeSubscriptionCounts[name]
      ? this.activeSubscriptionCounts[name] - 1
      : 0;
    if (this.activeSubscriptionCounts[name] == 0) {
      delete this.activeSubscriptionCounts[name];
    }
  }
}
